import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  ButtonProps,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { useStore } from '@lib/context/store-context'
import { useRegions } from 'medusa-react'
import ReactCountryFlag from 'react-country-flag'
import { listSaleChannelsByRegionId } from '@lib/util/region-sale-channel-handler'
import { SalesChannel } from '@medusajs/medusa'
import { isHabbitHostname } from '@lib/util/host'

type CountryOption = {
  country: string
  region: string
  label: string
}

const CountryItem = ({
  country,
  color // textColor
}: {
  country: string
  color?: string
}) => {
  return (
    <Flex gap={2} alignItems="center">
      <Text textStyle="linkItem" mt={1} color={color ?? 'black'}>
        {country.toUpperCase()}
      </Text>
      {country && (
        <ReactCountryFlag
          svg
          className="w-4 h-4"
          countryCode={country}
          alt={`${country} flag`}
        />
      )}
    </Flex>
  )
}

interface PropsType {
  bg?: string
  color?: string
  textColor?: string
}

const CountrySelectNav = ({
  bg,
  color = 'black',
  textColor,
  ...rest
}: PropsType & ButtonProps) => {
  const { countryCode, setRegion } = useStore()
  const { regions } = useRegions()
  const [current, setCurrent] = useState<CountryOption | undefined>(undefined)

  const options: CountryOption[] | undefined = useMemo(() => {
    return regions
      ?.map((r) => {
        return r.countries.map((c) => ({
          country: c.iso_2,
          region: r.id,
          label: c.display_name
        }))
      })
      .flat()
  }, [regions])

  useEffect(() => {
    if (countryCode) {
      const option = options?.find((o) => o.country === countryCode)
      setCurrent(option)
    }
  }, [countryCode, options])

  const handleChange = async (option?: CountryOption) => {
    if (option) {
      let salesChannels = await listSaleChannelsByRegionId(option?.region)
      const host = process.env.NEXT_PUBLIC_SITE || 'habbit'

      // Filter sales channels by host
      salesChannels = salesChannels.filter((sc: SalesChannel) =>
        sc.name.toLowerCase().includes(host)
      )

      const salesChannelIds = salesChannels.map((sc: SalesChannel) => sc.id)

      setRegion(option.region, option.country, salesChannelIds)
    }
  }

  return (
    <Menu>
      <MenuButton
        p={0}
        mt={0}
        as={Button}
        rightIcon={<ChevronDownIcon color={color} />}
        h={'auto'}
        w={'auto'}
        bg={bg || 'bg !important'}
        _hover={{
          bg: bg || 'bg !important',
          border: 0,
          boxShadow: '0 0 0 0'
        }}
        _focus={{
          bg: bg || 'bg !important',
          border: 0,
          boxShadow: '0 0 0 0'
        }}
        {...rest}
      >
        {current ? (
          <CountryItem country={current.country} color={textColor} />
        ) : countryCode ? (
          <CountryItem
            country={
              options?.find((o) => o.country === countryCode)?.country || ''
            }
            color={textColor}
          />
        ) : (
          ''
        )}
      </MenuButton>
      <MenuList color={color}>
        {options?.map((o) => {
          return (
            <MenuItem
              onClick={() => {
                handleChange(o)
              }}
              key={o.country}
            >
              <CountryItem country={o.country} color={color} />
            </MenuItem>
          )
        })}
      </MenuList>
    </Menu>
  )
}

export default CountrySelectNav
